import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';

const PointsManagement = () => {
  const [rankings, setRankings] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [adjustmentData, setAdjustmentData] = useState({
    points: 0,
    reason: ''
  });
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [adjustmentSuccess, setAdjustmentSuccess] = useState(false);
  const [resetData, setResetData] = useState({
    password: '',
    reason: ''
  });
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    fetchRankings();
  }, []);

  const fetchRankings = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPointsRanking();
      setRankings(response.data);
    } catch (err) {
      setError('获取积分排名失败');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setAdjustmentData({
      points: 0,
      reason: ''
    });
    setAdjustmentSuccess(false);
  };

  const handleAdjustmentChange = (e) => {
    setAdjustmentData({
      ...adjustmentData,
      [e.target.name]: e.target.name === 'points' ? parseInt(e.target.value, 10) || 0 : e.target.value
    });
  };

  const handleAdjustPoints = async (e) => {
    e.preventDefault();
    
    if (!selectedUser) return;
    
    try {
      setAdjustmentLoading(true);
      
      await apiService.adjustUserPoints({
        user_id: selectedUser.id,
        points: adjustmentData.points,
        reason: adjustmentData.reason
      });
      
      // 重新获取排名数据
      await fetchRankings();
      
      // 更新选中的用户数据
      const updatedUser = rankings.find(u => u.id === selectedUser.id);
      setSelectedUser(updatedUser);
      
      // 重置表单
      setAdjustmentData({
        points: 0,
        reason: ''
      });
      
      setAdjustmentSuccess(true);
      
      // 3秒后隐藏成功消息
      setTimeout(() => {
        setAdjustmentSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || '积分调整失败');
      console.error(err);
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const handleResetChange = (e) => {
    setResetData({
      ...resetData,
      [e.target.name]: e.target.value
    });
  };

  const handleShowResetConfirm = () => {
    setShowResetConfirm(true);
  };

  const handleCancelReset = () => {
    setShowResetConfirm(false);
    setResetData({
      password: '',
      reason: ''
    });
  };

  const handleResetAllPoints = async (e) => {
    e.preventDefault();
    
    try {
      setResetLoading(true);
      
      await apiService.resetAllPoints(resetData);
      
      // 重新获取排名数据
      await fetchRankings();
      
      // 重置表单和状态
      setResetData({
        password: '',
        reason: ''
      });
      setShowResetConfirm(false);
      
      // 如果有选中的用户，更新其数据
      if (selectedUser) {
        const updatedUser = rankings.find(u => u.id === selectedUser.id);
        setSelectedUser(updatedUser);
      }
    } catch (err) {
      setError(err.response?.data?.message || '积分重置失败');
      console.error(err);
    } finally {
      setResetLoading(false);
    }
  };

  // 搜索过滤
  const filteredRankings = rankings.filter(u => {
    const kw = search.trim().toLowerCase();
    if (!kw) return true;
    return (
      (u.username && u.username.toLowerCase().includes(kw)) ||
      (u.nickname && u.nickname.toLowerCase().includes(kw))
    );
  });

  if (loading && rankings.length === 0) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">加载中...</p>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-4">积分管理</h2>
      
      {error && <div className="alert alert-danger">{error}</div>}
      
      <div className="row">
        <div className="col-md-8">

          <div className="card mb-4">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
              <h4 className="mb-0">用户积分排名</h4>
              <input
                type="text"
                className="form-control form-control-sm ms-2"
                style={{ maxWidth: 220 }}
                placeholder="关键字搜索..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>排名</th>
                      <th>用户名</th>
                      <th>角色</th>
                      <th>总积分</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRankings.map((user, index) => (
                      <tr key={user.id} className={selectedUser?.id === user.id ? 'table-primary' : ''}>
                        <td>{index + 1}</td>
                        <td>{user.nickname || user.username}</td>
                        <td>
                          {user.role === 'admin' ? (
                            <span className="badge bg-danger">管理员</span>
                          ) : user.role === 'union' ? (
                            <span className="badge bg-warning">工会委员</span>
                          ) : (
                            <span className="badge bg-secondary">普通用户</span>
                          )}
                        </td>
                        <td>{user.total_points}</td>
                        <td>
                          <button 
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleSelectUser(user)}
                          >
                            调整积分
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="card-header bg-danger text-white">
              <h4 className="mb-0">全员积分重置</h4>
            </div>
            <div className="card-body">
              {showResetConfirm ? (
                <form onSubmit={handleResetAllPoints}>
                  <div className="alert alert-warning">
                    <strong>警告！</strong> 此操作将重置所有用户的积分为0，且不可撤销。请确认您要执行此操作。
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">管理员密码</label>
                    <input 
                      type="password" 
                      className="form-control" 
                      id="password" 
                      name="password"
                      value={resetData.password}
                      onChange={handleResetChange}
                      required
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="reset-reason" className="form-label">重置原因</label>
                    <textarea 
                      className="form-control" 
                      id="reset-reason" 
                      name="reason"
                      value={resetData.reason}
                      onChange={handleResetChange}
                      required
                    ></textarea>
                  </div>
                  
                  <div className="d-flex justify-content-end">
                    <button 
                      type="button" 
                      className="btn btn-secondary me-2"
                      onClick={handleCancelReset}
                    >
                      取消
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-danger"
                      disabled={resetLoading}
                    >
                      {resetLoading ? '重置中...' : '确认重置'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center">
                  <p>此功能将重置所有用户的积分为0，通常用于年度积分清零。</p>
                  <button 
                    className="btn btn-danger"
                    onClick={handleShowResetConfirm}
                  >
                    执行全员积分重置
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="col-md-4">
          {selectedUser ? (
            <div className="card sticky-top" style={{ top: '1rem' }}>
              <div className="card-header bg-primary text-white">
                <h4 className="mb-0">调整用户积分</h4>
              </div>
              <div className="card-body">
                <h5>{selectedUser.username}</h5>
                <p className="mb-3">当前积分: <strong>{selectedUser.total_points}</strong></p>
                
                {adjustmentSuccess && (
                  <div className="alert alert-success">积分调整成功！</div>
                )}
                
                <form onSubmit={handleAdjustPoints}>
                  <div className="mb-3">
                    <label htmlFor="points" className="form-label">积分变更</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      id="points" 
                      name="points"
                      value={adjustmentData.points}
                      onChange={handleAdjustmentChange}
                      required
                    />
                    <div className="form-text">正数为增加积分，负数为扣除积分</div>
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="reason" className="form-label">变更原因</label>
                    <textarea 
                      className="form-control" 
                      id="reason" 
                      name="reason"
                      value={adjustmentData.reason}
                      onChange={handleAdjustmentChange}
                      required
                    ></textarea>
                  </div>
                  
                  <button 
                    type="submit" 
                    className="btn btn-primary w-100"
                    disabled={adjustmentLoading}
                  >
                    {adjustmentLoading ? '提交中...' : '提交变更'}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body">
                <p className="text-center">请从左侧列表中选择一个用户进行积分调整</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-3">
        <Link to="/admin" className="btn btn-secondary">返回管理面板</Link>
      </div>
    </div>
  );
};

export default PointsManagement;
